import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Headphones,
  Globe,
  Users,
  ExternalLink,
  Shield,
  Zap,
} from "lucide-react";

const ContactInfo = () => {
  const contactMethods = [
    {
      icon: Phone,
      title: "Phone Support",
      primary: "+233 (200) 567-890",
      secondary: "+233 (272) 747-529",
      description: "Call us for immediate assistance",
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      action: "tel:+233 (200) 567-890",
    },
    {
      icon: Mail,
      title: "Email Support",
      primary: "support@txtconnect.net",
      secondary: "sales@txtconnect.net",
      description: "Send us an email anytime",
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      action: "mailto:support@txtconnect.net",
    },
    // {
    //   icon: Headphones,
    //   title: "Technical Support",
    //   primary: "tech@txtconnect.net",
    //   secondary: "Priority support available",
    //   description: "For technical issues and integration help",
    //   color: "from-purple-500 to-purple-600",
    //   bgColor: "from-purple-50 to-purple-100",
    //   action: "mailto:tech@txtconnect.net",
    // },
  ];

  const officeInfo = {
    address: "Abelemkpe, Accra, Ghana",
    hours: [{ day: "Monday - Friday", time: "8:00 AM - 5:00 PM" }],
    timeZone: "Greenwich Meridian Time (GMT+0)",
  };

  const stats = [
    { label: "Response Time", value: "< 1 Hour", icon: Clock },
    { label: "Customer Satisfaction", value: "98%", icon: Users },
    { label: "Support Languages", value: "3+", icon: Globe },
    { label: "Uptime Guarantee", value: "99.9%", icon: Shield },
  ];

  const supportFeatures = [
    {
      icon: Zap,
      title: "Priority Support",
      description:
        "Get faster response times with our priority support queue for urgent issues.",
    },
    {
      icon: Shield,
      title: "Secure Communication",
      description:
        "All communications are encrypted and your data privacy is our top priority.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description:
        "Our support team consists of messaging specialists and technical experts.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const handleContactClick = (action) => {
    if (action.startsWith("tel:") || action.startsWith("mailto:")) {
      window.location.href = action;
    }
  };

  return (
    <section
      id="contact-info"
      className="py-20 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-16 text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-red-100 to-blue-100 rounded-full border border-red-200/50">
              <MapPin className="mr-2 w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">
                Contact Information
              </span>
            </div>
            <h2 className="mb-4 text-3xl font-bold text-gray-800 md:text-4xl">
              Multiple Ways to Reach Us
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Choose the communication method that works best for you. Our
              support team is ready to help through any channel.
            </p>
          </motion.div>

          {/* Contact Methods Grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 gap-6 mb-16 md:grid-cols-2"
          >
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -8, scale: 1.02 }}
                className="cursor-pointer group"
                onClick={() => handleContactClick(method.action)}
              >
                <div
                  className={`p-6 rounded-2xl bg-gradient-to-br ${method.bgColor} border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 h-full`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <method.icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="mb-2 text-lg font-bold text-gray-800">
                    {method.title}
                  </h3>

                  <div className="mb-3 space-y-1">
                    <p className="text-sm font-medium text-gray-700">
                      {method.primary}
                    </p>
                    <p className="text-sm text-gray-600">{method.secondary}</p>
                  </div>

                  <p className="mb-4 text-sm text-gray-600">
                    {method.description}
                  </p>

                  <div className="flex items-center text-gray-500 transition-colors duration-300 group-hover:text-gray-700">
                    <span className="text-sm font-medium">Contact Now</span>
                    <ExternalLink className="ml-1 w-3 h-3" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Two Column Layout: Office Information and Support Features */}
          <div className="grid grid-cols-1 gap-8 mb-16 lg:grid-cols-2">
            {/* Office Information */}
            <motion.div
              variants={itemVariants}
              className="p-8 bg-white rounded-2xl border border-gray-100 shadow-xl"
            >
              <div className="flex items-center mb-6">
                <div className="flex justify-center items-center mr-4 w-12 h-12 bg-gradient-to-br from-red-500 to-blue-500 rounded-xl">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    Head Office
                  </h3>
                  <p className="text-gray-600">Business hours and location</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="flex items-center mb-2 font-semibold text-gray-700">
                    <MapPin className="mr-2 w-4 h-4 text-red-600" />
                    Location
                  </h4>
                  <p className="leading-relaxed text-gray-600">
                    {officeInfo.address}
                  </p>
                </div>

                <div>
                  <h4 className="flex items-center mb-3 font-semibold text-gray-700">
                    <Clock className="mr-2 w-4 h-4 text-red-600" />
                    Business Hours
                  </h4>
                  <div className="space-y-2">
                    {officeInfo.hours.map((hour, index) => (
                      <div
                        key={index}
                        className="flex justify-between p-2 text-sm bg-gray-50 rounded-lg"
                      >
                        <span className="text-gray-600">{hour.day}</span>
                        <span className="font-medium text-gray-800">
                          {hour.time}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    All times are in {officeInfo.timeZone}
                  </p>
                </div>

                <div className="p-4 bg-gradient-to-r from-red-50 to-blue-50 rounded-xl border border-red-200/50">
                  <div className="flex items-center mb-2">
                    <Clock className="mr-2 w-5 h-5 text-red-600" />
                    <span className="font-semibold text-gray-800">
                      24/7 Emergency Support
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    For critical issues that affect your messaging campaigns,
                    our emergency support is always available.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Support Features */}
            <motion.div
              variants={itemVariants}
              className="p-8 text-white bg-gradient-to-br from-gray-900 to-blue-900 rounded-2xl shadow-xl"
            >
              <div className="flex items-center mb-6">
                <div className="flex justify-center items-center mr-4 w-12 h-12 rounded-xl backdrop-blur-sm bg-white/20">
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Support Features</h3>
                  <p className="text-gray-300">
                    What makes our support special
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {supportFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    className="p-4 rounded-xl border backdrop-blur-sm bg-white/10 border-white/20"
                  >
                    <div className="flex items-start">
                      <div className="flex justify-center items-center mt-1 mr-3 w-8 h-8 rounded-lg bg-white/20">
                        <feature.icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-semibold text-white">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-300">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Support Statistics */}
          <motion.div
            variants={itemVariants}
            className="p-8 bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <div className="mb-8 text-center">
              <h3 className="mb-2 text-2xl font-bold text-gray-800">
                Support Statistics
              </h3>
              <p className="text-gray-600">
                Our commitment to excellence in numbers
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="p-6 text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
                >
                  <div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-gradient-to-br from-red-500 to-blue-500 rounded-xl">
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="mb-1 text-2xl font-bold text-gray-800">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactInfo;
